import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  /** Component-tree under guard. Wraps each top-level route in App.tsx
   *  so a render crash in one page never blanks the whole shell. */
  children: ReactNode;
  /** Human-readable label for the boundary, surfaced in the fallback
   *  UI ("Battle crashed", "Lobby crashed", …). */
  label?: string;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

// Plain React error boundary. Catches render-phase errors anywhere
// below it and shows a visible fallback with the message + stack —
// the user explicitly called out that "no error message" crashes
// (blank screen, silent JS error) are unacceptable. This is the
// last line of defense; without it, a thrown error during render
// unmounts the entire React tree and we get a blank screen.
//
// Note: error boundaries do NOT catch errors inside event handlers,
// async code, or `setState` callbacks — those still surface in the
// browser console. The boundary's job is to keep render-phase
// throws (the most common cause of blank-screen crashes) visible.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always log so devtools / production telemetry can pick it up.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`,
      error,
      info,
    );
    this.setState({ info });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          padding: 24,
          color: "#f0d99d",
          background: "#2d1b15",
          fontFamily: "ui-monospace, monospace",
          fontSize: 13,
          lineHeight: 1.5,
          minHeight: "100%",
          overflow: "auto",
        }}
      >
        <h1 style={{ fontSize: 18, marginTop: 0 }}>
          {this.props.label ? `${this.props.label} crashed` : "Something broke"}
        </h1>
        <p style={{ marginBottom: 12 }}>
          {error.name}: {error.message}
        </p>
        {error.stack && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "#1a0f05",
              padding: 12,
              border: "1px solid #4a2f1a",
              marginBottom: 12,
            }}
          >
            {error.stack}
          </pre>
        )}
        {info?.componentStack && (
          <details>
            <summary style={{ cursor: "pointer" }}>Component stack</summary>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                background: "#1a0f05",
                padding: 12,
                border: "1px solid #4a2f1a",
                marginTop: 8,
              }}
            >
              {info.componentStack}
            </pre>
          </details>
        )}
        <p style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 14px",
              background: "#4a2f1a",
              color: "#f0d99d",
              border: "1px solid #6b4525",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
            }}
          >
            Reload
          </button>
        </p>
      </div>
    );
  }
}
